// Signup — submits a pending request to the server; admin approves via System Admin → Users.
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../shared/api/client";
import { toast } from "../../shared/stores/toasts";

export default function SignupPage() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    desired_username: "",
    password: "",
    passwordConfirm: "",
    phone: "",
    department: "",
    reason: "",
    agree: false,
  });
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [usernameErr, setUsernameErr] = useState<string | null>(null);

  const validateUsername = (v: string) => {
    if (!v) return null; // optional
    if (v.length < 3) return "아이디는 3자 이상이어야 합니다";
    if (v.length > 50) return "아이디는 50자 이하여야 합니다";
    if (!/^[a-zA-Z0-9_.]+$/.test(v)) return "영문·숫자·언더스코어·점(.)만 사용 가능합니다";
    return null;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.agree) return toast.warn("이용약관에 동의해주세요");
    const uErr = validateUsername(form.desired_username);
    if (uErr) { setUsernameErr(uErr); return; }
    if (form.password.length < 8) return toast.warn("비밀번호는 8자 이상이어야 합니다");
    if (form.password !== form.passwordConfirm) return toast.warn("비밀번호가 일치하지 않습니다");
    setLoading(true);
    try {
      const res = await api.post<{ id: number; email: string }>("/auth/signup-request", {
        name: form.name,
        email: form.email,
        desired_username: form.desired_username || null,
        password: form.password,
        phone: form.phone || null,
        dept_request: form.department || null,
        reason: form.reason || null,
      });
      setSubmitted(String(res.data.id).padStart(8, "0"));
      toast.success("회원가입 신청 완료");
    } catch (e: any) {
      const msg = e?.response?.data?.error?.message ?? "신청 중 오류가 발생했습니다";
      toast.error("신청 실패", msg);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="timple-root">
        <div className="t-login" style={{ height: "100%" }}>
          <div className="t-login-brand">
            <img src="/timple-logo.jpg" alt="TIMPLE" style={{ height: 32, background: "white", padding: 6, borderRadius: 6 }}/>
            <div>
              <h1>승인 대기 중</h1>
              <p>관리자가 신청을 검토하면 이메일로 안내드립니다.</p>
            </div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>신청번호: <span className="mono">{submitted}</span></div>
          </div>
          <div className="t-login-pane">
            <div className="t-login-form" style={{ textAlign: "center" }}>
              <h2>신청이 접수되었습니다</h2>
              <p style={{ color: "var(--text-3)" }}>관리자의 승인 후 이메일로 안내드립니다. (평일 기준 1영업일 이내)</p>
              <button className="t-btn t-btn-primary" style={{ width: "100%", marginTop: 16 }} onClick={() => nav("/login")}>로그인 화면으로</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="timple-root">
      <div className="t-login" style={{ height: "100%" }}>
        <div className="t-login-brand">
          <img src="/timple-logo.jpg" alt="TIMPLE" style={{ height: 32, background: "white", padding: 6, borderRadius: 6 }}/>
          <div>
            <h1>회원가입 신청</h1>
            <p>관리자 승인 후 사내 계정이 활성화됩니다.</p>
            <ol style={{ marginTop: 16, paddingLeft: 20, lineHeight: 1.8 }}>
              <li>정보 입력</li>
              <li>승인 대기</li>
              <li>권한 부여</li>
              <li>로그인</li>
            </ol>
          </div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>이미 계정이 있으신가요? <Link to="/login" style={{ color: "white" }}>로그인</Link></div>
        </div>

        <div className="t-login-pane">
          <form onSubmit={submit} className="t-login-form">
            <h2>회원가입</h2>
            <p className="subtitle">관리자가 검토 후 권한을 부여합니다.</p>

            {/* Name */}
            <div style={{ marginBottom: 12 }}>
              <label className="t-label">이름 *</label>
              <input className="t-input" type="text" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>

            {/* Desired username */}
            <div style={{ marginBottom: 12 }}>
              <label className="t-label">희망 아이디 <span style={{ fontSize: 11, color: "var(--text-3)" }}>(선택 · 영문·숫자·._)</span></label>
              <input
                className="t-input"
                type="text"
                autoComplete="username"
                value={form.desired_username}
                onChange={(e) => {
                  setForm({ ...form, desired_username: e.target.value });
                  setUsernameErr(validateUsername(e.target.value));
                }}
                placeholder="예: hong.gildong"
                style={{ borderColor: usernameErr ? "var(--danger)" : undefined }}
              />
              {usernameErr && (
                <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 3 }}>{usernameErr}</div>
              )}
              {!usernameErr && form.desired_username && (
                <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3 }}>
                  로그인 아이디: <span style={{ fontFamily: "monospace", color: "var(--p-500)" }}>{form.desired_username}</span>
                </div>
              )}
            </div>

            {/* Email */}
            <div style={{ marginBottom: 12 }}>
              <label className="t-label">연락용 이메일 *</label>
              <input className="t-input" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 12 }}>
              <label className="t-label">비밀번호 * <span style={{ fontSize: 11, color: "var(--text-3)" }}>(8자 이상)</span></label>
              <input className="t-input" type="password" autoComplete="new-password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="최소 8자" required
                style={{ borderColor: form.password && form.password.length < 8 ? "var(--danger)" : undefined }} />
              {form.password && form.password.length < 8 && (
                <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 3 }}>8자 이상이어야 합니다</div>
              )}
            </div>

            {/* Password confirm */}
            <div style={{ marginBottom: 12 }}>
              <label className="t-label">비밀번호 확인 *</label>
              <input className="t-input" type="password" autoComplete="new-password" value={form.passwordConfirm}
                onChange={(e) => setForm({ ...form, passwordConfirm: e.target.value })} placeholder="비밀번호 재입력" required
                style={{ borderColor: form.passwordConfirm && form.passwordConfirm !== form.password ? "var(--danger)" : undefined }} />
              {form.passwordConfirm && form.passwordConfirm !== form.password && (
                <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 3 }}>비밀번호가 일치하지 않습니다</div>
              )}
            </div>

            {/* Phone */}
            <div style={{ marginBottom: 12 }}>
              <label className="t-label">전화번호</label>
              <input className="t-input" type="text" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>

            {/* Department */}
            <div style={{ marginBottom: 12 }}>
              <label className="t-label">희망 부서</label>
              <input className="t-input" type="text" value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })} />
            </div>

            {/* Reason */}
            <div style={{ marginBottom: 16 }}>
              <label className="t-label">가입 사유</label>
              <textarea className="t-textarea" value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 16 }}>
              <input type="checkbox" checked={form.agree} onChange={(e) => setForm({ ...form, agree: e.target.checked })} />
              개인정보 처리방침과 이용약관에 동의합니다
            </label>

            <button className="t-btn t-btn-primary" type="submit" disabled={loading} style={{ width: "100%", height: 42 }}>
              {loading ? "신청 중…" : "신청하기"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
